/**
 * SCP Buy Buttons - Enhanced Product Page Buy Buttons
 * Handles Buy Now (quick checkout) and enhanced add to cart functionality
 */

class SCPBuyButtons {
  constructor(element) {
    this.container = element;
    this.productId = this.container.dataset.productId;
    this.sectionId = this.container.dataset.sectionId;

    // Get form element - need to find the actual <form> inside <product-form>
    const productFormElement = this.container.closest("product-form");
    this.form = productFormElement
      ? productFormElement.querySelector("form")
      : this.container.querySelector("form");

    // Buttons
    this.addToCartButton = this.container.querySelector(
      "[data-scp-add-to-cart]"
    );
    this.buyNowButton = this.container.querySelector("[data-scp-buy-now]");
    this.wishlistButton = this.container.querySelector("[data-scp-wishlist]");

    // Quantity controls
    this.quantityInput = this.container.querySelector(
      "[data-scp-quantity-input]"
    );
    this.quantityMinus = this.container.querySelector(
      "[data-scp-quantity-minus]"
    );
    this.quantityPlus = this.container.querySelector(
      "[data-scp-quantity-plus]"
    );

    // Message container
    this.messageContainer = this.container.querySelector("[data-scp-message]");

    this.init();
  }

  init() {
    this.initQuantityControls();
    this.initBuyNow();
    this.initWishlist();
    this.initVariantChangeListener();
    // Add to cart is handled entirely by theme's product-form component
  }

  /**
   * Initialize quantity controls - Compatible with theme's m-quantity-input
   */
  initQuantityControls() {
    if (!this.quantityInput) return;

    // Check if theme's m-quantity-input custom element exists
    const quantityElement = this.quantityInput.closest("m-quantity-input");

    // Minus button
    if (this.quantityMinus) {
      this.quantityMinus.addEventListener("click", (e) => {
        e.preventDefault();
        this.decreaseQuantity();
      });
    }

    // Plus button
    if (this.quantityPlus) {
      this.quantityPlus.addEventListener("click", (e) => {
        e.preventDefault();
        this.increaseQuantity();
      });
    }

    // Direct input - validate on change
    this.quantityInput.addEventListener("change", () => {
      this.validateQuantity();
    });

    // Also validate on input for real-time feedback
    this.quantityInput.addEventListener("input", () => {
      this.updateQuantityButtons();
    });

    // Initialize button states
    this.updateQuantityButtons();
  }

  /**
   * Decrease quantity
   */
  decreaseQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    const step = parseInt(this.quantityInput.step) || 1;

    const newValue = Math.max(min, currentValue - step);
    this.quantityInput.value = newValue;
    this.updateQuantityButtons();

    // Trigger change event for theme compatibility
    this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Increase quantity
   */
  increaseQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 1;
    const max = parseInt(this.quantityInput.max) || Infinity;
    const step = parseInt(this.quantityInput.step) || 1;

    const newValue = Math.min(max, currentValue + step);
    this.quantityInput.value = newValue;
    this.updateQuantityButtons();

    // Trigger change event for theme compatibility
    this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Validate quantity input
   */
  validateQuantity() {
    const value = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || Infinity;
    const step = parseInt(this.quantityInput.step) || 1;

    let validValue = Math.max(min, Math.min(max, value));

    // Ensure value is a multiple of step
    const remainder = (validValue - min) % step;
    if (remainder !== 0) {
      validValue = validValue - remainder;
    }

    this.quantityInput.value = validValue;
    this.updateQuantityButtons();
  }

  /**
   * Update quantity button states
   */
  updateQuantityButtons() {
    if (!this.quantityInput) return;

    const value = parseInt(this.quantityInput.value) || 1;
    const min = parseInt(this.quantityInput.min) || 1;
    const max = parseInt(this.quantityInput.max) || Infinity;

    // Update minus button
    if (this.quantityMinus) {
      this.quantityMinus.disabled = value <= min;
    }

    // Update plus button
    if (this.quantityPlus) {
      this.quantityPlus.disabled = value >= max;
    }
  }

  /**
   * Initialize Buy Now functionality
   */
  initBuyNow() {
    if (!this.buyNowButton) return;

    this.buyNowButton.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      await this.handleBuyNow();
    });
  }

  /**
   * Handle Buy Now - Add to cart and redirect to checkout
   */
  async handleBuyNow() {
    if (!this.form) {
      console.error("SCP Buy Buttons: Form not found");
      return;
    }

    // Get the hidden input with variant ID
    const variantInput = this.form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) {
      this.showMessage("Please select a variant", "error");
      return;
    }

    const variantId = variantInput.value;
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    // Set loading state
    this.setLoadingState(this.buyNowButton, true);

    try {
      // Get form data for properties
      const formData = new FormData(this.form);
      const properties = this.getFormProperties(formData);

      // Add to cart
      const cartData = {
        items: [
          {
            id: parseInt(variantId),
            quantity: quantity,
            ...(properties && { properties }),
          },
        ],
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(cartData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description || data.message || "Failed to add to cart"
        );
      }

      // Trigger cart update event
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
        })
      );

      // Small delay to ensure cart is updated, then redirect
      setTimeout(() => {
        window.location.href = "/checkout";
      }, 300);
    } catch (error) {
      console.error("Buy Now error:", error);
      this.showMessage(
        error.message || "Failed to process. Please try again.",
        "error"
      );
      this.setLoadingState(this.buyNowButton, false);
    }
  }

  /**
   * Initialize variant change listener
   */
  initVariantChangeListener() {
    // Method 1: Listen for theme's variant change event on document
    document.addEventListener("variant:changed", (e) => {
      if (e.detail && e.detail.variant) {
        this.updateButtonStates(e.detail.variant);
      }
    });

    // Method 2: Listen on the form's parent product-form element
    const productFormElement = this.container.closest("product-form");
    if (productFormElement) {
      productFormElement.addEventListener("variant:changed", (e) => {
        if (e.detail && e.detail.variant) {
          this.updateButtonStates(e.detail.variant);
        }
      });
    }

    // Method 3: Watch for changes to the hidden variant input
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variantInput) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "value"
          ) {
            this.checkVariantAvailability(variantInput.value);
          }
        });
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });

      // Also listen for change event
      variantInput.addEventListener("change", () => {
        this.checkVariantAvailability(variantInput.value);
      });
    }

    // Method 4: MinimogTheme pubsub event (backup)
    if (
      window.MinimogEvents &&
      window.MinimogTheme &&
      window.MinimogTheme.pubSubEvents
    ) {
      try {
        if (typeof window.MinimogEvents.subscribe === "function") {
          window.MinimogEvents.subscribe(
            window.MinimogTheme.pubSubEvents.variantChange,
            ({ data }) => {
              if (data && data.variant) {
                this.updateButtonStates(data.variant);
              }
            }
          );
        }
      } catch (error) {
        console.error("Error setting up MinimogEvents listener:", error);
      }
    }
  }

  /**
   * Check variant availability by ID
   */
  checkVariantAvailability(variantId) {
    // Try to get variant data from the page
    const variantDataScript = document.querySelector(
      "script[data-selected-variant]"
    );
    if (variantDataScript) {
      try {
        const variant = JSON.parse(variantDataScript.textContent);
        if (variant && variant.id == variantId) {
          this.updateButtonStates(variant);
          return;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Fallback: Get all variants and find the matching one
    const productData = this.container.closest("[data-product-id]");
    if (productData) {
      // Try to find variant data in the page
      const variantScripts = document.querySelectorAll(
        'script[type="application/json"]'
      );
      for (const script of variantScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (Array.isArray(data)) {
            // This might be variants array
            const variant = data.find((v) => v.id == variantId);
            if (variant) {
              this.updateButtonStates(variant);
              return;
            }
          } else if (data.variants && Array.isArray(data.variants)) {
            // This might be product data with variants
            const variant = data.variants.find((v) => v.id == variantId);
            if (variant) {
              this.updateButtonStates(variant);
              return;
            }
          }
        } catch (e) {
          // Not JSON or not the right format, skip
        }
      }
    }
  }

  /**
   * Update button states based on variant availability
   */
  updateButtonStates(variant) {
    const isAvailable = variant.available;

    // Toggle the soldout class on the wrapper so the CSS rule hides
    // ATC + Buy Now and lets the Klaviyo BIS trigger take over.
    if (this.container) {
      this.container.classList.toggle("scp-buy-buttons--soldout", !isAvailable);
    }

    // Update Add to Cart button
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !isAvailable;

      const buttonText = this.addToCartButton.querySelector(
        ".scp-buy-buttons__button-text"
      );
      if (buttonText) {
        if (!isAvailable) {
          buttonText.textContent =
            this.addToCartButton.dataset.soldoutText || "Sold out";
        } else {
          buttonText.textContent =
            this.addToCartButton.dataset.atcText || "Add to cart";
        }
      }
    }

    // Update Buy Now button
    if (this.buyNowButton) {
      this.buyNowButton.disabled = !isAvailable;
    }

    // Update quantity controls
    if (this.quantityInput) {
      this.quantityInput.disabled = !isAvailable;
      if (this.quantityMinus) this.quantityMinus.disabled = !isAvailable;
      if (this.quantityPlus) this.quantityPlus.disabled = !isAvailable;
    }
  }

  /**
   * Initialize wishlist functionality - Leverages theme's Wishlist class
   */
  initWishlist() {
    if (!this.wishlistButton) return;

    const productHandle = this.container.dataset.productHandle;
    if (!productHandle) {
      console.error("SCP Buy Buttons: Product handle not found");
      return;
    }

    // Set the product handle on the button for theme compatibility
    this.wishlistButton.setAttribute("data-product-handle", productHandle);

    // Check initial state
    this.updateWishlistState();

    // Use theme's wishlist toggle functionality
    this.wishlistButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (window.MinimogTheme && window.MinimogTheme.Wishlist) {
        // Get current state - use `added-to-wishlist` (the class theme's
        // toggleButtonState actually manages). The legacy `is-active` class is
        // never toggled by the click flow, so checking it here would break the
        // "remove" direction.
        const isCurrentlyActive =
          this.wishlistButton.classList.contains("added-to-wishlist");
        const newState = !isCurrentlyActive;

        // Use theme's toggleButtonState method
        window.MinimogTheme.Wishlist.toggleButtonState(
          this.wishlistButton,
          newState
        );

        // Keep legacy `is-active` class in sync with the active state so any
        // other CSS/JS relying on it stays consistent.
        this.syncWishlistButton(this.wishlistButton, newState);

        // Update wishlist count
        window.MinimogTheme.Wishlist.updateWishlistCount();

        // Sync other wishlist buttons for this product
        document
          .querySelectorAll(`[data-product-handle="${productHandle}"]`)
          .forEach((btn) => {
            if (
              btn !== this.wishlistButton &&
              btn.classList.contains("m-wishlist-button")
            ) {
              this.syncWishlistButton(btn, newState);
            }
          });
      } else {
        // Fallback: manual toggle
        this.manualWishlistToggle(productHandle);
      }
    });

    // Listen for wishlist changes from theme
    document.addEventListener("wishlist:updated", () => {
      this.updateWishlistState();
    });
  }

  /**
   * Manual wishlist toggle fallback
   */
  manualWishlistToggle(productHandle) {
    const storageKey = "m-wishlist-products";
    let wishlist = [];

    try {
      wishlist = JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch (e) {
      console.error("Error reading wishlist:", e);
    }

    const isInWishlist = wishlist.includes(productHandle);

    if (isInWishlist) {
      // Remove from wishlist
      wishlist = wishlist.filter((handle) => handle !== productHandle);
      this.syncWishlistButton(this.wishlistButton, false);
    } else {
      // Add to wishlist
      wishlist.push(productHandle);
      this.syncWishlistButton(this.wishlistButton, true);
    }

    localStorage.setItem(storageKey, JSON.stringify(wishlist));
  }

  /**
   * Update wishlist button state - Uses theme's storage
   */
  updateWishlistState() {
    if (!this.wishlistButton) return;

    const productHandle = this.container.dataset.productHandle;
    if (!productHandle) return;

    // Check if in wishlist using theme's storage key
    const storageKey = "m-wishlist-products";
    let isInWishlist = false;

    try {
      const wishlist = JSON.parse(localStorage.getItem(storageKey) || "[]");
      isInWishlist = wishlist.includes(productHandle);
    } catch (e) {
      console.error("Error checking wishlist:", e);
    }

    // Update button state
    this.syncWishlistButton(this.wishlistButton, isInWishlist);
  }

  /**
   * Sync wishlist button visual state
   */
  syncWishlistButton(button, isActive) {
    if (!button) return;

    if (isActive) {
      button.classList.add("is-active", "added-to-wishlist");
      button.setAttribute("aria-label", "Remove from wishlist");
    } else {
      button.classList.remove("is-active", "added-to-wishlist");
      button.setAttribute("aria-label", "Add to wishlist");
    }
  }

  /**
   * Set loading state on button (for Buy Now only)
   */
  setLoadingState(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.classList.add("is-loading");
      button.disabled = true;
    } else {
      button.classList.remove("is-loading");
      button.disabled = false;
    }
  }

  /**
   * Get form properties (custom fields, etc.)
   */
  getFormProperties(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[") && value) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    return Object.keys(properties).length > 0 ? properties : undefined;
  }

  /**
   * Show message to user
   */
  showMessage(message, type = "error") {
    if (!this.messageContainer) {
      // Create message container if it doesn't exist
      this.messageContainer = document.createElement("div");
      this.messageContainer.setAttribute("data-scp-message", "");
      this.container.appendChild(this.messageContainer);
    }

    this.messageContainer.className = `scp-buy-buttons__message scp-buy-buttons__message--${type}`;
    this.messageContainer.textContent = message;
    this.messageContainer.style.display = "block";

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (this.messageContainer) {
        this.messageContainer.style.display = "none";
      }
    }, 5000);
  }
}

/**
 * Initialize all SCP Buy Buttons
 */
function initSCPBuyButtons() {
  const containers = document.querySelectorAll("[data-scp-buy-buttons]");
  containers.forEach((container) => {
    if (!container.scpBuyButtons) {
      container.scpBuyButtons = new SCPBuyButtons(container);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSCPBuyButtons);
} else {
  initSCPBuyButtons();
}

// Re-initialize on dynamic content load
document.addEventListener("shopify:section:load", initSCPBuyButtons);

// Export for use in other scripts
window.SCPBuyButtons = SCPBuyButtons;
window.initSCPBuyButtons = initSCPBuyButtons;
